import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ── Helper ────────────────────────────────────────────────────────────────────

function unwrap<T>(res: { data: ApiResponse<T> }): T {
  return res.data.data;
}

function parseFileNameFromDisposition(contentDisposition?: string) {
  if (!contentDisposition) {
    return undefined;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1];
}

export interface ReportExportFile {
  blob: Blob;
  fileName: string;
}

export interface JournalUserOptionDto {
  id: string;
  firstName: string;
  otherNames: string;
  email: string;
  userName: string;
}

// ============================================================================
// TYPES — Chart of Accounts
// ============================================================================

export interface AccountDto {
  id: string;
  accountCode: string;
  accountName: string;
  accountType: number;
  accountTypeName: string;
  parentAccountId: string | null;
  parentAccountName: string | null;
  description: string | null;
  isGroup: boolean;
  isActive: boolean;
  balance: number;
  isDebitBalance: boolean;
  childCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface AccountSummaryDto {
  id: string;
  accountCode: string;
  accountName: string;
  accountType: number;
  accountTypeName: string;
  isGroup: boolean;
  isActive: boolean;
  balance: number;
}

export interface AccountSearchOptionDto {
  id: string;
  code: string;
  name: string;
  type: string;
  isPosting: boolean;
}

export interface AccountTreeDto {
  id: string;
  accountCode: string;
  accountName: string;
  accountType: number;
  accountTypeName: string;
  isGroup: boolean;
  isActive: boolean;
  balance: number;
  children: AccountTreeDto[];
}

export interface CreateAccountDto {
  accountCode: string;
  accountName: string;
  accountType: number;
  parentAccountId: string | null;
  description: string | null;
  isGroup: boolean;
}

export interface UpdateAccountDto {
  id: string;
  accountName: string;
  description: string | null;
  parentAccountId: string | null;
  isGroup: boolean;
}

// ============================================================================
// TYPES — Journal Entries
// ============================================================================

export interface JournalEntryDto {
  id: string;
  entryNumber: string;
  postingDate: string;
  status: number;
  statusName: string;
  narration: string | null;
  referenceNumber: string | null;
  sourceDocumentType: string | null;
  sourceDocumentId: string | null;
  totalDebit: number;
  totalCredit: number;
  submittedByUserName: string | null;
  submittedAt: string | null;
  approvedByUserName: string | null;
  approvedAt: string | null;
  rejectedByUserName: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  reversedByUserName: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  reversalJournalEntryId: string | null;
  lines: JournalEntryLineDto[];
  createdAt: string;
  updatedAt: string | null;
}

export interface JournalEntrySummaryDto {
  id: string;
  entryNumber: string;
  postingDate: string;
  status: number;
  statusName: string;
  narration: string | null;
  referenceNumber: string | null;
  totalDebit: number;
  totalCredit: number;
  lineCount: number;
  submittedByUserName: string | null;
  approvedByUserName: string | null;
  createdByUserName: string | null;
  createdAt: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface JournalEntryListParams {
  pageNumber: number;
  pageSize: number;
  search?: string;
  status?: number;
  startDate?: string;
  endDate?: string;
  createdByUserId?: string;
}

export interface JournalEntryLineDto {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debitAmount: number;
  creditAmount: number;
  costCentreId: string | null;
  costCentreName: string | null;
  partyType: number;
  partyTypeName: string;
  partyId: string | null;
  partyName: string | null;
  remarks: string | null;
}

export interface CreateJournalEntryDto {
  postingDate: string;
  narration: string | null;
  referenceNumber: string | null;
  lines: CreateJournalEntryLineDto[];
}

export interface CreateJournalEntryLineDto {
  accountId: string;
  debitAmount: number;
  creditAmount: number;
  costCentreId: string | null;
  partyType: number;
  partyId: string | null;
  remarks: string | null;
}

export interface RejectJournalEntryDto {
  journalEntryId: string;
  rejectionReason: string;
}

export interface ReverseJournalEntryDto {
  journalEntryId: string;
  reversalReason: string;
  reversalDate: string | null;
}

// ============================================================================
// TYPES — Cost Centres
// ============================================================================

// ============================================================================
// TYPES — Companies
// ============================================================================

export interface CompanyDto {
  id: string;
  companyName: string;
  abbreviation: string | null;
  registrationNumber: string | null;
  taxId: string | null;
  phone: string | null;
  phone2: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  isActive: boolean;
  costCentreCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface CompanySummaryDto {
  id: string;
  companyName: string;
  abbreviation: string | null;
  isActive: boolean;
}

export interface CreateCompanyDto {
  companyName: string;
  abbreviation: string | null;
  registrationNumber: string | null;
  taxId: string | null;
  phone: string | null;
  phone2: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
}

export interface UpdateCompanyDto {
  id: string;
  companyName: string;
  abbreviation: string | null;
  registrationNumber: string | null;
  taxId: string | null;
  phone: string | null;
  phone2: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
}

// ============================================================================
// TYPES — Cost Centres
// ============================================================================

export interface CostCentreDto {
  id: string;
  costCentreCode: string;
  costCentreName: string;
  parentCostCentreId: string | null;
  parentCostCentreName: string | null;
  companyId: string | null;
  companyName: string | null;
  description: string | null;
  isActive: boolean;
  isGroup: boolean;
  childCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface CostCentreTreeDto {
  id: string;
  costCentreCode: string;
  costCentreName: string;
  companyId: string | null;
  companyName: string | null;
  description: string | null;
  isActive: boolean;
  isGroup: boolean;
  children: CostCentreTreeDto[];
}

export interface CreateCostCentreDto {
  costCentreCode: string;
  costCentreName: string;
  parentCostCentreId: string | null;
  companyId: string | null;
  description: string | null;
  isGroup: boolean;
}

export interface UpdateCostCentreDto {
  id: string;
  costCentreName: string;
  parentCostCentreId: string | null;
  companyId: string | null;
  description: string | null;
  isGroup: boolean;
}

export interface CostCentreAllocationDto {
  costCentreId: string;
  costCentreCode: string;
  costCentreName: string;
  totalDebits: number;
  totalCredits: number;
  netAmount: number;
  accountBreakdown: CostCentreAccountBreakdownDto[];
}

export interface CostCentreAccountBreakdownDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  debitTotal: number;
  creditTotal: number;
  netAmount: number;
}

// ============================================================================
// TYPES — Bank Accounts
// ============================================================================

export interface BankAccountDto {
  id: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  branchName: string | null;
  branchCode: string | null;
  swiftCode: string | null;
  currency: string;
  glAccountId: string | null;
  glAccountName: string | null;
  currentBalance: number;
  lastReconciledDate: string | null;
  lastReconciledBalance: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface BankAccountSummaryDto {
  id: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  currency: string;
  currentBalance: number;
  lastReconciledDate: string | null;
  isActive: boolean;
}

export interface CreateBankAccountDto {
  accountName: string;
  accountNumber: string;
  bankName: string;
  branchName: string | null;
  branchCode: string | null;
  swiftCode: string | null;
  currency: string;
  glAccountId: string | null;
  openingBalance: number;
}

export interface UpdateBankAccountDto {
  id: string;
  accountName: string;
  bankName: string;
  branchName: string | null;
  branchCode: string | null;
  swiftCode: string | null;
  glAccountId: string | null;
}

export interface BankTransactionDto {
  id: string;
  bankAccountId: string;
  bankAccountName: string;
  transactionDate: string;
  valueDate: string | null;
  referenceNumber: string | null;
  description: string | null;
  debitAmount: number;
  creditAmount: number;
  runningBalance: number;
  reconciliationStatus: number;
  reconciliationStatusName: string;
  reconciledJournalEntryId: string | null;
  reconciledJournalEntryNumber: string | null;
  reconciledByUserName: string | null;
  reconciledAt: string | null;
  chequeNumber: string | null;
  chequeDate: string | null;
  chequeCleared: boolean | null;
  chequeClearedDate: string | null;
  createdAt: string;
}

export interface CreateBankTransactionDto {
  bankAccountId: string;
  transactionDate: string;
  valueDate: string | null;
  referenceNumber: string | null;
  description: string | null;
  debitAmount: number;
  creditAmount: number;
  chequeNumber: string | null;
  chequeDate: string | null;
}

export interface BankReconciliationStatementDto {
  bankAccountId: string;
  bankAccountName: string;
  accountNumber: string;
  asOfDate: string;
  bankStatementBalance: number;
  systemBalance: number;
  difference: number;
  unreconciledBankTransactions: BankTransactionDto[];
  unreconciledSystemEntries: JournalEntrySummaryDto[];
  unclearedCheques: BankTransactionDto[];
}

export interface BankClearanceDto {
  bankAccountId: string;
  bankAccountName: string;
  fromDate: string;
  toDate: string;
  totalCheques: number;
  clearedCheques: number;
  unclearedCheques: number;
  clearedAmount: number;
  unclearedAmount: number;
  cheques: BankTransactionDto[];
}

// ============================================================================
// TYPES — Budgets
// ============================================================================

export interface BudgetDto {
  id: string;
  budgetName: string;
  fiscalYear: number;
  startDate: string;
  endDate: string;
  frequency: number;
  frequencyName: string;
  description: string | null;
  isApproved: boolean;
  approvedByUserName: string | null;
  approvedAt: string | null;
  lineCount: number;
  totalBudgeted: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface BudgetSummaryDto {
  id: string;
  budgetName: string;
  fiscalYear: number;
  frequency: number;
  frequencyName: string;
  isApproved: boolean;
  totalBudgeted: number;
  createdAt: string;
}

export interface BudgetLineDto {
  id: string;
  budgetId: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  costCentreId: string | null;
  costCentreName: string | null;
  periodNumber: number;
  periodStartDate: string;
  periodEndDate: string;
  budgetedAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number;
}

export interface CreateBudgetDto {
  budgetName: string;
  fiscalYear: number;
  startDate: string;
  endDate: string;
  frequency: number;
  description: string | null;
  lines: CreateBudgetLineDto[];
}

export interface CreateBudgetLineDto {
  accountId: string;
  costCentreId: string | null;
  periodNumber: number;
  budgetedAmount: number;
}

export interface UpdateBudgetLineDto {
  budgetLineId: string;
  budgetedAmount: number;
}

export interface BudgetVarianceReportDto {
  budgetId: string;
  budgetName: string;
  fiscalYear: number;
  startDate: string;
  endDate: string;
  totalBudgeted: number;
  totalActual: number;
  totalVariance: number;
  overallVariancePercent: number;
  accountVariances: BudgetVarianceByAccountDto[];
  costCentreVariances: BudgetVarianceByCostCentreDto[];
}

export interface BudgetVarianceByAccountDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  budgetedAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number;
  periods: BudgetVariancePeriodDto[];
}

export interface BudgetVarianceByCostCentreDto {
  costCentreId: string;
  costCentreCode: string;
  costCentreName: string;
  budgetedAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number;
}

export interface BudgetVariancePeriodDto {
  periodNumber: number;
  periodStart: string;
  periodEnd: string;
  budgetedAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number;
}

// ============================================================================
// TYPES — Financial Statements
// ============================================================================

export interface TrialBalanceReportDto {
  asOfDate: string;
  costCentreId: string | null;
  costCentreName: string | null;
  totalDebits: number;
  totalCredits: number;
  isBalanced: boolean;
  rows: TrialBalanceRowDto[];
}

export interface TrialBalanceRowDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: number;
  accountTypeName: string;
  debitBalance: number;
  creditBalance: number;
}

export interface BalanceSheetReportDto {
  asOfDate: string;
  costCentreId: string | null;
  costCentreName: string | null;
  assets: BalanceSheetSectionDto;
  liabilities: BalanceSheetSectionDto;
  equity: BalanceSheetSectionDto;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  retainedEarnings: number;
  isBalanced: boolean;
}

export interface BalanceSheetSectionDto {
  sectionName: string;
  total: number;
  lines: BalanceSheetLineDto[];
  subSections: BalanceSheetSubSectionDto[];
}

export interface BalanceSheetSubSectionDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  total: number;
  lines: BalanceSheetLineDto[];
}

export interface BalanceSheetLineDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  balance: number;
}

export interface ProfitAndLossReportDto {
  startDate: string;
  endDate: string;
  costCentreId: string | null;
  costCentreName: string | null;
  income: ProfitAndLossSectionDto;
  expenses: ProfitAndLossSectionDto;
  grossProfit: number;
  netProfit: number;
  netProfitMarginPercent: number;
}

export interface ProfitAndLossSectionDto {
  sectionName: string;
  total: number;
  lines: ProfitAndLossLineDto[];
  groups: ProfitAndLossGroupDto[];
}

export interface ProfitAndLossGroupDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  total: number;
  lines: ProfitAndLossLineDto[];
}

export interface ProfitAndLossLineDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: number;
  children?: ProfitAndLossLineDto[] | null;
}

export interface CashFlowReportDto {
  startDate: string;
  endDate: string;
  openingCashBalance: number;
  operatingActivities: CashFlowSectionDto;
  investingActivities: CashFlowSectionDto;
  financingActivities: CashFlowSectionDto;
  netCashChange: number;
  closingCashBalance: number;
}

export interface CashFlowSectionDto {
  sectionName: string;
  netAmount: number;
  lines: CashFlowLineDto[];
}

export interface CashFlowLineDto {
  description: string;
  accountId: string | null;
  accountCode: string | null;
  amount: number;
}

// ── General Ledger Reports ────────────────────────────────────────────────────

export interface GeneralLedgerReportDto {
  startDate: string;
  endDate: string;
  accountId: string | null;
  accountCode: string | null;
  accountName: string | null;
  accountTypeName: string | null;
  costCentreId: string | null;
  costCentreName: string | null;
  openingBalance: number;
  totalDebits: number;
  totalCredits: number;
  closingBalance: number;
  entries: GeneralLedgerEntryDto[];
}

export interface GeneralLedgerEntryDto {
  postingDate: string;
  journalEntryNumber: string;
  journalEntryId: string;
  sourceModule: string | null;
  sourceDocumentNumber: string | null;
  accountCode: string;
  accountName: string;
  narration: string | null;
  referenceNumber: string | null;
  debitAmount: number;
  creditAmount: number;
  runningBalance: number;
  costCentreName: string | null;
  partyName: string | null;
  partyType: number;
}

export interface CustomerLedgerSummaryDto {
  startDate: string;
  endDate: string;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  customers: CustomerLedgerRowDto[];
}

export interface CustomerLedgerRowDto {
  customerId: string;
  customerName: string;
  openingBalance: number;
  invoiced: number;
  payments: number;
  creditNotes: number;
  closingBalance: number;
  transactions: CustomerLedgerTransactionDto[];
}

export interface CustomerLedgerTransactionDto {
  date: string;
  documentType: string;
  documentNumber: string;
  description: string | null;
  debitAmount: number;
  creditAmount: number;
  runningBalance: number;
}

export interface SupplierLedgerSummaryDto {
  startDate: string;
  endDate: string;
  totalPurchased: number;
  totalPaid: number;
  totalOutstanding: number;
  suppliers: SupplierLedgerRowDto[];
}

export interface SupplierLedgerRowDto {
  supplierId: string;
  supplierName: string;
  openingBalance: number;
  purchases: number;
  payments: number;
  closingBalance: number;
  transactions: SupplierLedgerTransactionDto[];
}

export interface SupplierLedgerTransactionDto {
  date: string;
  documentType: string;
  documentNumber: string;
  description: string | null;
  debitAmount: number;
  creditAmount: number;
  runningBalance: number;
}

// ── Sales & Purchase Registers ────────────────────────────────────────────────

export interface SalesRegisterDto {
  startDate: string;
  endDate: string;
  totalInvoiced: number;
  totalTax: number;
  totalDiscount: number;
  totalNet: number;
  invoiceCount: number;
  invoices: SalesRegisterRowDto[];
}

export interface SalesRegisterRowDto {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  customerName: string;
  status: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
}

export interface ItemWiseSalesRegisterDto {
  startDate: string;
  endDate: string;
  totalQuantity: number;
  totalAmount: number;
  lineCount: number;
  items: ItemWiseSalesRowDto[];
}

export interface ItemWiseSalesRowDto {
  invoiceNumber: string;
  invoiceDate: string;
  customerName: string;
  itemDescription: string;
  variety: string | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountAmount: number;
  taxAmount: number;
  lineTotal: number;
}

export interface PurchaseRegisterDto {
  startDate: string;
  endDate: string;
  totalPurchased: number;
  totalVat: number;
  totalNet: number;
  invoiceCount: number;
  invoices: PurchaseRegisterRowDto[];
}

export interface PurchaseRegisterRowDto {
  invoiceId: string;
  invoiceNumber: string;
  supplierInvoiceNumber: string | null;
  invoiceDate: string;
  dueDate: string;
  supplierName: string;
  status: string;
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
}

export interface ItemWisePurchaseRegisterDto {
  startDate: string;
  endDate: string;
  totalQuantity: number;
  totalAmount: number;
  lineCount: number;
  items: ItemWisePurchaseRowDto[];
}

export interface ItemWisePurchaseRowDto {
  invoiceNumber: string;
  supplierInvoiceNumber: string | null;
  invoiceDate: string;
  supplierName: string;
  itemDescription: string;
  batchNumber: string | null;
  quantity: number;
  unit: string;
  unitCost: number;
  vatAmount: number;
  lineTotal: number;
}

export interface ApSummaryDto {
  asOf: string;
  totalOutstanding: number;
  outstandingInvoiceCount: number;
  totalOverdue: number;
  overdueInvoiceCount: number;
  agingBuckets: ApAgingBucketDto[];
  topCreditors: ApSupplierItemDto[];
}

export interface ApAgingBucketDto {
  label: string;
  daysFrom: number;
  daysTo: number;
  amount: number;
  invoiceCount: number;
}

export interface ApSupplierItemDto {
  supplierId: string;
  supplierName: string;
  outstanding: number;
  overdue: number;
  overdueInvoiceCount: number;
  oldestDueDate: string | null;
}

export interface SalesInvoiceTrendsDto {
  startDate: string;
  endDate: string;
  granularity: string;
  periods: InvoiceTrendPeriodDto[];
}

export interface InvoiceTrendPeriodDto {
  period: string;
  periodStart: string;
  periodEnd: string;
  invoiceCount: number;
  totalAmount: number;
  averageInvoiceValue: number;
  growthPercent: number;
}

export interface PurchaseInvoiceTrendsDto {
  startDate: string;
  endDate: string;
  granularity: string;
  periods: PurchaseInvoiceTrendPeriodDto[];
}

export interface PurchaseInvoiceTrendPeriodDto {
  period: string;
  periodStart: string;
  periodEnd: string;
  invoiceCount: number;
  totalAmount: number;
  averageInvoiceValue: number;
  growthPercent: number;
}

// ============================================================================
// API CLIENT
// ============================================================================

export const accountingApi = {
  // ── Chart of Accounts ───────────────────────────────────────────────────────
  getAccounts: async () => unwrap<AccountSummaryDto[]>(await apiClient.get("/Accounts")),
  getAccountById: async (id: string) => unwrap<AccountDto>(await apiClient.get(`/Accounts/${id}`)),
  getAccountTree: async () => unwrap<AccountTreeDto[]>(await apiClient.get("/Accounts/tree")),
  getPostableAccounts: async () => unwrap<AccountSummaryDto[]>(await apiClient.get("/Accounts/postable")),
  searchAccounts: async (params: {
    query?: string;
    postingOnly?: boolean;
    activeOnly?: boolean;
    pageSize?: number;
    pageNumber?: number;
  }) => unwrap<AccountSearchOptionDto[]>(await apiClient.get("/accounting/chart-of-accounts/search", { params })),
  createAccount: async (dto: CreateAccountDto) => unwrap<AccountDto>(await apiClient.post("/Accounts", dto)),
  updateAccount: async (id: string, dto: UpdateAccountDto) => unwrap<AccountDto>(await apiClient.put(`/Accounts/${id}`, dto)),
  deactivateAccount: async (id: string) => unwrap<boolean>(await apiClient.post(`/Accounts/${id}/deactivate`)),

  // ── Journal Entries ─────────────────────────────────────────────────────────
  getJournalEntriesPaged: async (params: JournalEntryListParams) =>
    unwrap<PagedResult<JournalEntrySummaryDto>>(await apiClient.get("/journal-entries", { params })),
  getJournalUserOptions: async () =>
    unwrap<JournalUserOptionDto[]>(await apiClient.get("/journal-entries/user-options")),
  getJournalEntryById: async (id: string) => unwrap<JournalEntryDto>(await apiClient.get(`/journal-entries/${id}`)),
  getJournalEntriesByStatus: async (status: number) => unwrap<JournalEntrySummaryDto[]>(await apiClient.get(`/journal-entries/by-status/${status}`)),
  getJournalEntriesByDateRange: async (startDate: string, endDate: string) =>
    unwrap<JournalEntrySummaryDto[]>(await apiClient.get("/journal-entries/by-date-range", { params: { startDate, endDate } })),
  createJournalEntry: async (dto: CreateJournalEntryDto) => unwrap<JournalEntryDto>(await apiClient.post("/journal-entries", dto)),
  updateJournalEntry: async (id: string, dto: CreateJournalEntryDto) => unwrap<JournalEntryDto>(await apiClient.put(`/journal-entries/${id}`, dto)),
  submitJournalEntry: async (id: string) => unwrap<JournalEntryDto>(await apiClient.post(`/journal-entries/${id}/submit`)),
  approveJournalEntry: async (id: string) => unwrap<JournalEntryDto>(await apiClient.post(`/journal-entries/${id}/approve`)),
  rejectJournalEntry: async (dto: RejectJournalEntryDto) =>
    unwrap<JournalEntryDto>(await apiClient.post(`/journal-entries/${dto.journalEntryId}/reject`, dto)),
  reverseJournalEntry: async (dto: ReverseJournalEntryDto) =>
    unwrap<JournalEntryDto>(await apiClient.post(`/journal-entries/${dto.journalEntryId}/reverse`, dto)),

  // ── Companies ───────────────────────────────────────────────────────────────
  getCompanies: async () => unwrap<CompanyDto[]>(await apiClient.get("/companies")),
  getCompanyById: async (id: string) => unwrap<CompanyDto>(await apiClient.get(`/companies/${id}`)),
  getCompanySummaries: async () => unwrap<CompanySummaryDto[]>(await apiClient.get("/companies/summaries")),
  createCompany: async (dto: CreateCompanyDto) => unwrap<CompanyDto>(await apiClient.post("/companies", dto)),
  updateCompany: async (id: string, dto: UpdateCompanyDto) => unwrap<CompanyDto>(await apiClient.put(`/companies/${id}`, dto)),
  deactivateCompany: async (id: string) => unwrap<boolean>(await apiClient.post(`/companies/${id}/deactivate`)),

  // ── Cost Centres ────────────────────────────────────────────────────────────
  getCostCentres: async () => unwrap<CostCentreDto[]>(await apiClient.get("/cost-centres")),
  getCostCentreById: async (id: string) => unwrap<CostCentreDto>(await apiClient.get(`/cost-centres/${id}`)),
  getCostCentreTree: async () => unwrap<CostCentreTreeDto[]>(await apiClient.get("/cost-centres/tree")),
  createCostCentre: async (dto: CreateCostCentreDto) => unwrap<CostCentreDto>(await apiClient.post("/cost-centres", dto)),
  updateCostCentre: async (id: string, dto: UpdateCostCentreDto) => unwrap<CostCentreDto>(await apiClient.put(`/cost-centres/${id}`, dto)),
  deactivateCostCentre: async (id: string) => unwrap<boolean>(await apiClient.post(`/cost-centres/${id}/deactivate`)),
  getCostCentreAllocationReport: async (startDate: string, endDate: string) =>
    unwrap<CostCentreAllocationDto[]>(await apiClient.get("/cost-centres/allocation-report", { params: { startDate, endDate } })),

  // ── Bank Accounts ───────────────────────────────────────────────────────────
  getBankAccounts: async () => unwrap<BankAccountSummaryDto[]>(await apiClient.get("/bank-accounts")),
  getBankAccountById: async (id: string) => unwrap<BankAccountDto>(await apiClient.get(`/bank-accounts/${id}`)),
  createBankAccount: async (dto: CreateBankAccountDto) => unwrap<BankAccountDto>(await apiClient.post("/bank-accounts", dto)),
  updateBankAccount: async (id: string, dto: UpdateBankAccountDto) => unwrap<BankAccountDto>(await apiClient.put(`/bank-accounts/${id}`, dto)),
  deactivateBankAccount: async (id: string) => unwrap<boolean>(await apiClient.post(`/bank-accounts/${id}/deactivate`)),
  getBankTransactions: async (bankAccountId: string, startDate?: string, endDate?: string) =>
    unwrap<BankTransactionDto[]>(await apiClient.get(`/bank-accounts/${bankAccountId}/transactions`, { params: { startDate, endDate } })),
  getUnreconciledTransactions: async (bankAccountId: string) =>
    unwrap<BankTransactionDto[]>(await apiClient.get(`/bank-accounts/${bankAccountId}/transactions/unreconciled`)),
  createBankTransaction: async (bankAccountId: string, dto: CreateBankTransactionDto) =>
    unwrap<BankTransactionDto>(await apiClient.post(`/bank-accounts/${bankAccountId}/transactions`, dto)),
  reconcileTransaction: async (dto: { bankTransactionId: string; journalEntryId: string }) =>
    unwrap<BankTransactionDto>(await apiClient.post("/bank-accounts/transactions/reconcile", dto)),
  clearCheque: async (dto: { bankTransactionId: string; clearedDate: string }) =>
    unwrap<BankTransactionDto>(await apiClient.post("/bank-accounts/transactions/clear-cheque", dto)),
  getReconciliationStatement: async (bankAccountId: string, asOfDate: string) =>
    unwrap<BankReconciliationStatementDto>(await apiClient.get(`/bank-accounts/${bankAccountId}/reconciliation-statement`, { params: { asOfDate } })),
  getBankClearance: async (bankAccountId: string, fromDate: string, toDate: string) =>
    unwrap<BankClearanceDto>(await apiClient.get(`/bank-accounts/${bankAccountId}/clearance`, { params: { fromDate, toDate } })),

  // ── Budgets ─────────────────────────────────────────────────────────────────
  getBudgets: async () => unwrap<BudgetSummaryDto[]>(await apiClient.get("/Budgets")),
  getBudgetById: async (id: string) => unwrap<BudgetDto>(await apiClient.get(`/Budgets/${id}`)),
  getBudgetsByFiscalYear: async (year: number) => unwrap<BudgetSummaryDto[]>(await apiClient.get(`/Budgets/by-fiscal-year/${year}`)),
  createBudget: async (dto: CreateBudgetDto) => unwrap<BudgetDto>(await apiClient.post("/Budgets", dto)),
  updateBudgetLine: async (dto: UpdateBudgetLineDto) => unwrap<BudgetLineDto>(await apiClient.put("/Budgets/lines", dto)),
  approveBudget: async (id: string) => unwrap<BudgetDto>(await apiClient.post(`/Budgets/${id}/approve`)),
  getBudgetVarianceReport: async (id: string) => unwrap<BudgetVarianceReportDto>(await apiClient.get(`/Budgets/${id}/variance-report`)),

  // ── Financial Statements ────────────────────────────────────────────────────
  getTrialBalance: async (asOfDate: string, costCentreId?: string) =>
    unwrap<TrialBalanceReportDto>(await apiClient.get("/financial-statements/trial-balance", { params: { asOfDate, costCentreId } })),
  getBalanceSheet: async (asOfDate: string, costCentreId?: string) =>
    unwrap<BalanceSheetReportDto>(await apiClient.get("/financial-statements/balance-sheet", { params: { asOfDate, costCentreId } })),
  getProfitAndLoss: async (startDate: string, endDate: string, costCentreId?: string) =>
    unwrap<ProfitAndLossReportDto>(await apiClient.get("/financial-statements/profit-and-loss", { params: { startDate, endDate, costCentreId } })),
  getCashFlow: async (startDate: string, endDate: string) =>
    unwrap<CashFlowReportDto>(await apiClient.get("/financial-statements/cash-flow", { params: { startDate, endDate } })),
  getGeneralLedger: async (
    startDate: string,
    endDate: string,
    accountId?: string,
    costCentreId?: string,
    sourceModule?: string,
    searchText?: string,
    pageNumber?: number,
    pageSize?: number,
  ) =>
    unwrap<GeneralLedgerReportDto>(await apiClient.get("/financial-statements/general-ledger", {
      params: { startDate, endDate, accountId, costCentreId, sourceModule, searchText, pageNumber, pageSize }
    })),
  getCustomerLedger: async (startDate: string, endDate: string, customerId?: string) =>
    unwrap<CustomerLedgerSummaryDto>(await apiClient.get("/financial-statements/customer-ledger", { params: { startDate, endDate, customerId } })),
  getSupplierLedger: async (startDate: string, endDate: string, supplierId?: string) =>
    unwrap<SupplierLedgerSummaryDto>(await apiClient.get("/financial-statements/supplier-ledger", { params: { startDate, endDate, supplierId } })),
  getSalesRegister: async (startDate: string, endDate: string, customerId?: string) =>
    unwrap<SalesRegisterDto>(await apiClient.get("/financial-statements/sales-register", { params: { startDate, endDate, customerId } })),
  getItemWiseSalesRegister: async (startDate: string, endDate: string, customerId?: string) =>
    unwrap<ItemWiseSalesRegisterDto>(await apiClient.get("/financial-statements/item-wise-sales-register", { params: { startDate, endDate, customerId } })),
  getPurchaseRegister: async (startDate: string, endDate: string, supplierId?: string) =>
    unwrap<PurchaseRegisterDto>(await apiClient.get("/financial-statements/purchase-register", { params: { startDate, endDate, supplierId } })),
  getItemWisePurchaseRegister: async (startDate: string, endDate: string, supplierId?: string) =>
    unwrap<ItemWisePurchaseRegisterDto>(await apiClient.get("/financial-statements/item-wise-purchase-register", { params: { startDate, endDate, supplierId } })),
  getApSummary: async (asOf?: string) =>
    unwrap<ApSummaryDto>(await apiClient.get("/financial-statements/accounts-payable-summary", { params: { asOf } })),
  getSalesInvoiceTrends: async (startDate: string, endDate: string, granularity?: string) =>
    unwrap<SalesInvoiceTrendsDto>(await apiClient.get("/financial-statements/sales-invoice-trends", { params: { startDate, endDate, granularity } })),
  getPurchaseInvoiceTrends: async (startDate: string, endDate: string, granularity?: string) =>
    unwrap<PurchaseInvoiceTrendsDto>(await apiClient.get("/financial-statements/purchase-invoice-trends", { params: { startDate, endDate, granularity } })),

  exportTrialBalance: async (params: {
    asOfDate: string;
    costCentreId?: string;
    format: "xlsx" | "pdf";
  }): Promise<ReportExportFile> => {
    const response = await apiClient.get<Blob>("/financial-statements/trial-balance/export", {
      params,
      responseType: "blob",
    });

    return {
      blob: response.data,
      fileName: parseFileNameFromDisposition(response.headers["content-disposition"]) ?? `trial-balance.${params.format}`,
    };
  },

  exportBalanceSheet: async (params: {
    asOfDate: string;
    costCentreId?: string;
    format: "xlsx" | "pdf";
  }): Promise<ReportExportFile> => {
    const response = await apiClient.get<Blob>("/financial-statements/balance-sheet/export", {
      params,
      responseType: "blob",
    });

    return {
      blob: response.data,
      fileName: parseFileNameFromDisposition(response.headers["content-disposition"]) ?? `balance-sheet.${params.format}`,
    };
  },

  exportProfitAndLoss: async (params: {
    startDate: string;
    endDate: string;
    costCentreId?: string;
    format: "xlsx" | "pdf";
  }): Promise<ReportExportFile> => {
    const response = await apiClient.get<Blob>("/financial-statements/profit-and-loss/export", {
      params,
      responseType: "blob",
    });

    return {
      blob: response.data,
      fileName: parseFileNameFromDisposition(response.headers["content-disposition"]) ?? `profit-and-loss.${params.format}`,
    };
  },

  exportGeneralLedger: async (params: {
    startDate: string;
    endDate: string;
    accountId?: string;
    costCentreId?: string;
    sourceModule?: string;
    searchText?: string;
    format: "xlsx" | "pdf";
  }): Promise<ReportExportFile> => {
    const response = await apiClient.get<Blob>("/financial-statements/general-ledger/export", {
      params,
      responseType: "blob",
    });

    return {
      blob: response.data,
      fileName: parseFileNameFromDisposition(response.headers["content-disposition"]) ?? `${params.accountId ? "account-ledger" : "general-ledger"}.${params.format}`,
    };
  },
};
